import React, { useCallback, useEffect, useState } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import Autocomplete from "react-google-autocomplete";
import FormRender from "@/components/Form/Renderer/RenderForm";
import { AddressFormConfig } from "@/utils/constant/formConfig.constant";
import { GOOGLE_MAP_API_KEY } from "@/utils/constant/globalEnv.constant";
import { RoundLoader } from "@/common/ui/Loader";
import { motion } from "framer-motion";

const containerStyle = {
  width: "100%",
  height: "400px",
};

const center = {
  lat: 20.5937,
  lng: 78.9629,
};

const GoogleAddress: React.FC<{ onClose: (props?: any) => void; parentSetValue: any; parentGetValues: any }> = ({
  onClose,
  parentSetValue,
  parentGetValues,
}) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
    libraries: ["places", "geometry", "marker"],
    id: "google-map-script",
  });
  const [position, setPosition] = useState(center);
  const [map, setMap] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [defaultFormValue, setDefaultFormValues] = useState(null);

  const onLoad = useCallback((map: any) => {
    const bounds = new window.google.maps.LatLngBounds(center);
    map.fitBounds(bounds);
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const handlePlaceSelected = (place: google.maps.places.PlaceResult) => {
    if (place.geometry && place.geometry.location) {
      const newPosition = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      setPosition(newPosition);

      const addressComponents = place.address_components || [];
      let streetNumber = "";
      let route = "";
      let city = "";
      let state = "";
      let country = "";
      let postalCode = "";
      let landmark = "";

      addressComponents.forEach((component) => {
        const types = component.types;

        if (types.includes("street_number")) {
          streetNumber = component.long_name;
        }
        if (types.includes("landmark")) {
          landmark = component.long_name;
        }
        if (types.includes("route")) {
          route = component.long_name;
        }
        if (types.includes("locality")) {
          city = component.long_name;
        }
        if (types.includes("administrative_area_level_1")) {
          state = component.long_name;
        }
        if (types.includes("country")) {
          country = component.long_name;
        }
        if (types.includes("postal_code")) {
          postalCode = component.long_name;
        }
      });
      const newFormValues: any = {
        name: landmark || "",
        geoRadius: "1000",
        address: place.formatted_address,
        lineAddress: `${streetNumber} ${route}`.trim() || place.formatted_address || "",
        city,
        state,
        country,
        zipCode: postalCode,
        latitude: newPosition.lat.toString(),
        longitude: newPosition.lng.toString(),
      };
      setDefaultFormValues(newFormValues);

      if (map) {
        map.panTo(newPosition);
      }
    }
  };

  const handleSubmit = (data: any) => {
    Object.entries(data).forEach(([key, value]) => {
      const isFieldRegistered = parentGetValues(key) !== undefined;
      if (isFieldRegistered) {
        parentSetValue(key, value, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    });
    onClose();
  };

  const handleMapClick = (event: any) => {
    const clickedPosition = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };

    setPosition(clickedPosition);
    const placesService = new google.maps.places.PlacesService(map);

    // First, use Geocoder to get placeId
    const geocoder: any = new google.maps.Geocoder();
    geocoder.geocode({ location: clickedPosition }, (results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
      if (status === "OK" && results[0]) {
        const placeId = results[0].place_id;

        // Use Places Service to get detailed place information
        placesService.getDetails(
          {
            placeId: placeId,
            fields: ["address_components", "formatted_address", "geometry", "name", "place_id"],
          },
          (place: google.maps.places.PlaceResult | null, placeStatus: google.maps.places.PlacesServiceStatus) => {
            if (placeStatus === google.maps.places.PlacesServiceStatus.OK && place) {
              // Extract address components
              const addressComponents = place.address_components || [];
              let streetNumber = "";
              let route = "";
              let city = "";
              let state = "";
              let country = "";
              let postalCode = "";
              let landmark = "";

              addressComponents.forEach((component) => {
                const types = component.types;

                if (types.includes("street_number")) {
                  streetNumber = component.long_name;
                }
                if (types.includes("landmark")) {
                  landmark = component.long_name;
                }
                if (types.includes("route")) {
                  route = component.long_name;
                }
                if (types.includes("locality")) {
                  city = component.long_name;
                }
                if (types.includes("administrative_area_level_1")) {
                  state = component.long_name;
                }
                if (types.includes("country")) {
                  country = component.long_name;
                }
                if (types.includes("postal_code")) {
                  postalCode = component.long_name;
                }
              });

              // Create form values object
              const newFormValues: any = {
                name: landmark || "",
                geoRadius: "1000",
                address: place.formatted_address,
                lineAddress: `${streetNumber} ${route}`.trim() || place.formatted_address || "",
                city,
                state,
                country,
                zipCode: postalCode,
                latitude: clickedPosition.lat.toString(),
                longitude: clickedPosition.lng.toString(),
                placeId: place.place_id,
              };

              setDefaultFormValues(newFormValues);

              // Pan map to new position
              if (map) {
                map.panTo(clickedPosition);
              }
            }
          }
        );
      }
    });
  };

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const currentPosition = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setPosition(currentPosition);
          setIsLoading(false);
        },
        (error) => {
          console.error("Error getting current position:", error);
          setIsLoading(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        }
      );
    } else {
      console.log("Geolocation is not supported");
      setIsLoading(false);
    }
  }, []);

  if (!isLoaded || isLoading) {
    return <RoundLoader className=" min-h-60" />;
  }
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.99 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.1 }}
      className="p-4"
    >
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={position}
        options={{
          zoomControl: true,
          streetViewControl: true,
          mapTypeControl: true,
          fullscreenControl: true,
        }}
        zoom={5}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={handleMapClick}
      >
        <Marker position={position} visible animation={google.maps.Animation.DROP} />
        <Autocomplete
          apiKey={GOOGLE_MAP_API_KEY}
          aria-placeholder="Search Location"
          onPlaceSelected={handlePlaceSelected}
          options={{
            types: ["establishment", "geocode"],
          }}
          className=" w-1/2 p-2 m-2.5 border bg-white border-gray-300 rounded mb-4 absolute top-0 right-0"
        />
      </GoogleMap>
      <FormRender
        handleFormSubmit={handleSubmit}
        handleCancel={onClose}
        formConfig={AddressFormConfig}
        className="pt-4"
        btnContainerClass="!py-4 sticky bottom-0 bg-white"
        defaultValues={defaultFormValue}
      />
    </motion.div>
  );
};

export default GoogleAddress;
